import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, take, takeUntil, tap } from 'rxjs/operators';
import { AwvDefaultConfigurationService } from 'src/app/admin/dashboards/awv-default-configuration/services/awv-default-configuration.service';
import { AddProvidersComponent } from 'src/app/dialogs/patient/add-providers/add-providers.component';
import { ClinicalTask } from 'src/app/models/clients/clinical-task.model';
import { PatientGap } from 'src/app/models/patient-gap.model';
import { CoverageTypes, Languages, Patient } from 'src/app/models/patient.model';
import { PENDING_ACTIVATION_LABEL, PatientStatusCatalogItem } from 'src/app/models/patient-status.model';
import { ClientServicesService } from 'src/app/services/admin/client-services.service';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { DataService } from 'src/app/services/data.service';
import { InitialDataModel } from 'src/app/services/models/initial-data-model.service';
import { PatientService } from 'src/app/services/patient.service';
import { ProgramService } from 'src/app/services/programs/program.service';
import { SnackService } from 'src/app/services/snack.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AreYouSureModalComponent } from 'src/app/shared/are-you-sure-modal/are-you-sure-modal.component';
import { Departament } from 'src/app/shared/components/client-detail/client-department-list/client-department.model';
import { MrnValidatorService } from 'src/app/shared/validators/mrn.validator';
import {
  apcmValidator,
  forcedInactiveChoiceValidator,
  onHoldDateRequiredValidator,
} from 'src/app/shared/validators/programs.validators';
import { ClinicalTimeAlertComponent } from './dialogs/disenrollment-clinical-time-alert/clinical-time-alert.component';
import { PatientDisenrollmentComponent } from './dialogs/patient-disenrollment/patient-disenrollment.component';

@Component({
  selector: 'app-basic-information',
  templateUrl: './basic-information.component.html',
  styleUrls: ['./basic-information.component.scss'],
})
export class BasicInformationComponent implements OnInit, OnDestroy {
  @Input()
  patient: Patient;
  @Input()
  expandPanel = false;
  @Output()
  toSavePatient = new EventEmitter<Patient>();
  @Output()
  changeEmail = new EventEmitter<{ email: string; patientId: string }>();
  @Output()
  journeyUpdateNeeded = new EventEmitter<void>();
  basicinfoForm: UntypedFormGroup;
  genderTypes: string[] = [];
  providers: Patient[] = [];
  languages: string[] = Object.values(Languages);
  coverages: string[] = Object.values(CoverageTypes);
  showChangeEmailButton = false;
  programs$: Observable<any[]>;
  filteredProgramOptions$: Observable<any[]>;
  availablePrograms: any[] = [];
  languageModel = { label: 'Language', showLabel: true, appearance: 'outline', showOnlyActive: false };
  statusGroups$: Observable<{ label: string; options: string[] }[]>;
  groupExpanded: Record<string, boolean> = {};
  statusCatalog: PatientStatusCatalogItem[] = [];
  // Guards the race where Save could fire before the catalog config loads (validator would no-op).
  statusConfigLoaded = false;
  forcedInactiveEngaged = false;
  // Shows the status field in its error state as soon as the forced-inactive-choice rule fails —
  // the failing validator lives on the parent `status` group, so the default matcher (which only
  // reads the label control) wouldn't paint it red, and we don't want to wait for the field to be
  // touched. Falls back to the standard touched/dirty behavior for the control's own errors.
  forcedInactiveErrorMatcher: ErrorStateMatcher = {
    isErrorState: (control) => {
      const groupError = !!control?.parent?.hasError('inactiveChoiceRequired');
      const selfError = !!control && control.invalid && (control.dirty || control.touched);
      return groupError || selfError;
    },
  };
  unsubscribe = new Subject<void>();
  clientDepartments: Departament[] = [];
  apcmTiers: { name: string; value: string }[] = [];
  chipLabel = '';
  chipColor = '';
  isEnableAwv = false;

  constructor(
    private fb: UntypedFormBuilder,
    private dataService: DataService,
    private initialModelService: InitialDataModel,
    private utilsService: UtilsService,
    private dialog: MatDialog,
    private authService: NewAuthService,
    private programService: ProgramService,
    public clientServicesService: ClientServicesService,
    private patientService: PatientService,
    private mrnValidator: MrnValidatorService,
    private snackService: SnackService,
    private awvConfiguration: AwvDefaultConfigurationService
  ) {}

  /** Selectable inactive reasons for the forced-choice flow: not active, not deceased, not deprecated. */
  get forcedInactiveOptions(): PatientStatusCatalogItem[] {
    return this.statusCatalog
      .filter((item) => !item.active && !item.deceased && !item.deprecated)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * True when the clinician must pick an inactive reason before saving: a catalog is present
   * (inner safety net) and all programs were removed from an active patient. Always mandatory —
   * choosing an inactive reason on full program removal is required, not feature-flagged.
   */
  get showForcedInactiveChoice(): boolean {
    return this.statusCatalog.length > 0 && this.forcedInactiveEngaged;
  }

  /**
   * The patient's current stored label when it is NOT one of the selectable options (a deprecated,
   * or unrecognized/orphan status). Surfaced as a hidden option so mat-select recognizes the value
   * and displays it instead of rendering blank. Null once the label is a selectable option (the
   * groups already render it) or before the catalog loads.
   */
  get unlistedStatusLabel(): string | null {
    const current = this.basicinfoForm?.get('status')?.get('label')?.value;
    if (!current || this.statusCatalog.length === 0) {
      return null;
    }
    const selectable = this.statusCatalog.filter((item) => !item.deprecated).map((item) => item.label);
    return selectable.includes(current) ? null : current;
  }

  get programs(): string[] {
    return (this.basicinfoForm.get('status') as UntypedFormGroup)?.getRawValue()
      ? Object.keys((this.basicinfoForm.get('status') as UntypedFormGroup)?.getRawValue())
          .filter(
            (key) =>
              this.availablePrograms.some((program) => program.value === key || program.value.slice(0, 3) === key) &&
              this.basicinfoForm.get('status').get(key).value === true
          )
          .map((key) =>
            this.availablePrograms.find((program) => {
              if (key === 'rpm') {
                return program.value === this.basicinfoForm.get('status').get('rpm_category').value;
              }
              return program.value === key;
            })
          )
      : [];
  }

  ngOnInit(): void {
    this.enableAwvForm();
    this.genderTypes = this.dataService.genderTypes;
    this.apcmTiers = this.dataService.apcmTiers;
    this.getClientDepartment();
    this.getPatientStatusLabel();
    this.initializeForm();
    this.providers = (Object.values(this.initialModelService.allClientUsers) as Patient[])
      .filter((client) => client.roles.isClient)
      .sort((clientA, clientB) => this.utilsService.sortUsersByAlphabeticOrder(clientA, clientB));
    this.setBasicFormlisteners();
    this.setFilteredProgramOption();
    this.labelOBProgram();
    if (this.patient?.ccm_to_apcm_consent?.consent === true) {
      this.basicinfoForm.get('ccm_to_apcm_consent')?.disable();
    }
    if (this.patient?.direct_referral === true) {
      this.basicinfoForm.get('direct_referral')?.disable();
    }
  }

  enableAwvForm(): void {
    this.awvConfiguration
      .isEnableAwvForm(this.patientService.currentPatientService?.client_responsible_id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((isEnable) => {
        this.isEnableAwv = isEnable;
        this.getPrograms();
      });
  }

  labelOBProgram(): void {
    const now = new Date();
    const startLastPregnancy = new Date(
      this.patient?.start_last_pregnancy?.seconds * 1000 +
        Math.floor(this.patient?.start_last_pregnancy?.nanoseconds / 1e6)
    );
    const diffMs = now.getTime() - startLastPregnancy.getTime();
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(totalDays / 7);
    const days = totalDays % 7;
    const label: string[] = [];
    if (weeks > 0) {
      label.push(`${weeks} week${weeks !== 1 ? 's' : ''}`);
    }
    if (days > 0) {
      label.push(`${days} day${days !== 1 ? 's' : ''}`);
    }
    if (weeks >= 8) {
      this.chipLabel = '+8 weeks';
    } else if (label.length === 2) {
      this.chipLabel = `${label[0]} and ${label[1]}`;
    } else {
      this.chipLabel = label[0] ?? '';
    }
    this.chipColor = weeks >= 8 ? 'warn' : 'default';
  }

  getPatientStatusLabel(): void {
    const statusConfig$ = this.dataService.getPatientStatusConfig().pipe(shareReplay({ bufferSize: 1, refCount: true }));
    // The dropdown groups statuses into two collapsible sections by prefix:
    // anything starting with "Active" goes to Active, everything else to Inactive.
    // The list itself is still driven by welby_configuration/patient_status.
    this.statusGroups$ = statusConfig$.pipe(
      map((config) => this.buildStatusGroups(config.items ?? [])),
      tap((groups) => this.initStatusGroupExpansion(groups))
    );
    // Catalog drives the forced inactive-reason choice.
    statusConfig$.pipe(takeUntil(this.unsubscribe)).subscribe((config) => {
      this.statusCatalog = config.catalog ?? [];
      this.statusConfigLoaded = true;
      // A stored legacy label (e.g. "Inactive - Other") isn't a selectable option, so mat-select
      // would render blank. Normalize it to its canonical label ("Inactive") so it displays and
      // is selected. Runs here because the catalog loads asynchronously after the form is built.
      this.normalizeStoredStatusLabel();
      // Catalog/flag load asynchronously after the form is built; re-run the status validator so
      // the forced-choice rule applies to a patient that is already program-less on open.
      this.basicinfoForm?.get('status')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  toggleStatusGroup(label: string): void {
    this.groupExpanded[label] = !this.groupExpanded[label];
  }

  trackByStatusKey(_index: number, item: PatientStatusCatalogItem): string {
    return item.key;
  }

  trackByStatusLabel(_index: number, label: string): string {
    return label;
  }

  /** Color class for the status dot, mirroring the design mockup's colored dots. */
  statusDotClass(label: string): string {
    if (!label) {
      return 'gray';
    }
    if (label === 'Active - On Hold') {
      return 'amber';
    }
    if (label === 'Active - Awaiting Device') {
      return 'blue';
    }
    if (label.toLowerCase().startsWith('active')) {
      return 'green';
    }
    if (label === 'Inactive - Deceased' || label === 'Deceased') {
      return 'red';
    }
    return 'gray';
  }

  getPrograms(): void {
    this.programs$ = this.programService.getPrograms();
    this.programs$.pipe(take(1)).subscribe((programs) => {
      this.availablePrograms = programs.filter((p) => this.isEnableAwv === true || p.value !== 'awv');
      this.availablePrograms.forEach((programObj) => {
        const programKey = programObj.value;
        const programValue = this.patient.status?.[programKey] ?? '';
        const lastUpdateKey = `${programKey}_last_update`;
        const lastUpdateValue = this.patient.status?.[lastUpdateKey] ?? '';
        const disenrollmentKey = `disenrollment_${programKey}_comments`;
        const disenrollmentValue = this.patient.status?.[disenrollmentKey] ?? '';
        (this.basicinfoForm.get('status') as UntypedFormGroup).addControl(
          programKey,
          new UntypedFormControl(programValue, [])
        );
        (this.basicinfoForm.get('status') as UntypedFormGroup).addControl(
          lastUpdateKey,
          new UntypedFormControl(lastUpdateValue, [])
        );
        (this.basicinfoForm.get('status') as UntypedFormGroup).addControl(
          disenrollmentKey,
          new UntypedFormControl(disenrollmentValue, [])
        );
        if (programKey === 'apcm') {
          (this.basicinfoForm.get('status') as UntypedFormGroup).addControl(
            'tier_apcm',
            new UntypedFormControl(this.patient.status?.tier_apcm || 'tier_2')
          );
          if (!this.authService.user?.roles.isAdmin) {
            (this.basicinfoForm.get('status') as UntypedFormGroup).get('tier_apcm').disable();
          }
        }
      });
    });
  }

  setFilteredProgramOption(): void {
    this.filteredProgramOptions$ = this.basicinfoForm.get('programInput').valueChanges.pipe(
      filter((data) => data && data.length > 0),
      map((data) =>
        this.availablePrograms.filter(
          (program) =>
            program.name.toLowerCase().includes(data.toLowerCase()) ||
            program.value.toLowerCase().includes(data.toLowerCase())
        )
      )
    );
  }

  selectProgram(program: string): void {
    const normalizedProgram = program.startsWith('rpm') ? program.slice(0, 3) : program;
    this.basicinfoForm.get('status').get(normalizedProgram)?.setValue(true);
    if (program.startsWith('rpm')) {
      (this.basicinfoForm.get('status') as UntypedFormGroup).get('rpm_category')?.setValue(program);
    }
    this.basicinfoForm.get('programInput').reset();
    this.evaluateForcedInactiveChoice();
  }

  removeProgram(program: string): void {
    if (program.startsWith('rpm')) {
      (this.basicinfoForm.get('status') as UntypedFormGroup).get('rpm')?.setValue(false);
      (this.basicinfoForm.get('status') as UntypedFormGroup).get('rpm_category')?.setValue(null);
    } else {
      (this.basicinfoForm.get('status') as UntypedFormGroup).get(program)?.setValue(false);
    }
    this.evaluateForcedInactiveChoice();
  }

  revertProgram(program: string, priorPatient: Patient): void {
    const originalPatient = priorPatient;
    if (originalPatient && originalPatient.status) {
      if (program.startsWith('rpm')) {
        const originalRpmValue = originalPatient.status.rpm;
        const originalRpmCategory = originalPatient.status.rpm_category;
        (this.basicinfoForm.get('status') as UntypedFormGroup).get('rpm')?.setValue(originalRpmValue);
        (this.basicinfoForm.get('status') as UntypedFormGroup).get('rpm_category')?.setValue(originalRpmCategory);
        this.patient.status.rpm = originalRpmValue;
        this.patient.status.rpm_category = originalRpmCategory;
      } else {
        const originalValue = originalPatient.status[program];
        (this.basicinfoForm.get('status') as UntypedFormGroup).get(program)?.setValue(originalValue);
        this.patient.status[program] = originalValue;
      }
    }
  }

  initializeForm(): void {
    this.basicinfoForm = this.fb.group({
      firstName: [this.patient.firstName ?? '', Validators.required],
      lastName: [this.patient.lastName ?? '', Validators.required],
      middleName: [this.patient.middleName ?? ''],
      gender: [this.patient.gender ?? '', Validators.required],
      email: [this.patient.email ?? '', [Validators.required, Validators.email]],
      language: [this.patient.language ?? ''],
      active_rpm: [this.patient.active_rpm ?? ''],
      height_inches: [this.patient.height_inches ?? ''],
      dob: [typeof this.patient.dob === 'string' ? new Date(this.patient.dob) : (this.patient.dob?.toDate() ?? '')],
      weight_lbs: [this.patient.weight_lbs ?? ''],
      notes: [this.patient.notes ?? ''],
      sms_active: [''],
      timezone: [this.patient.timezone ?? ''],
      primary_coverage_type: [this.patient.primary_coverage_type ?? ''],
      isPregnant: [this.patient.isPregnant ?? false],
      start_last_pregnancy: [this.patient.start_last_pregnancy ?? ''],
      emr_data: this.fb.group({
        mrn: new UntypedFormControl(this.fillEMRdataFromPatient() ?? '', {
          validators: [],
          asyncValidators: [
            this.mrnValidator.createMrnAsyncValidator(this.patient?.user_id, this.patient?.client_responsible_id),
          ],
          updateOn: 'change',
        }),
        emr_guid: [this.fillEMRdataGUIDFromPatient() ?? ''],
        renderingproviderid: [this.patient.emr_data?.renderingproviderid ?? ''],
        renderingprovidername: [this.patient.emr_data?.renderingprovidername ?? ''],
      }),
      hubspot_id: [this.patient.hubspot_id ?? ''],
      client_department: [this.patient?.department?.name ?? ''],
      status: this.fb.group(
        {
          rpm_category: [this.patient.status?.rpm ? (this.patient.status?.rpm_category ?? 'rpms') : ''],
          rpm: [this.patient.status?.rpm ?? ''],
          rpm_last_update: [this.patient.status?.rpm_last_update ?? ''],
          deceased: [this.patient.status?.deceased ?? ''],
          label: [this.patient.status?.label ?? ''],
          dateOnHold: [this.patient.status?.on_hold?.end_date.toDate() ?? ''],
        },
        {
          validators: [
            apcmValidator,
            onHoldDateRequiredValidator,
            forcedInactiveChoiceValidator(() => ({
              enabled: this.statusCatalog.length > 0,
              allowedLabels: this.acceptableInactiveLabels(),
              hasActivePrograms: this.hasAnyActiveProgram(),
            })),
          ],
        }
      ),
      programInput: [''],
      contact_consent: this.fb.group({
        email: [this.patient.contact_consent?.email ?? ''],
        phone: [this.patient.contact_consent?.phone ?? ''],
        sms: [this.patient.contact_consent?.sms ?? ''],
      }),
      ccm_to_apcm_consent: [this.patient.ccm_to_apcm_consent?.consent ?? ''],
      direct_referral: [this.patient.direct_referral ?? false],
    });
    // Pending Activation is the exception: the patient is inactive precisely because assigning their
    // programs is the step that comes next, so the input they need cannot start disabled.
    if (!this.patient.status.active && this.patient.status.label !== PENDING_ACTIVATION_LABEL) {
      this.basicinfoForm.get('programInput').disable();
    }
  }

  getClientDepartment(): void {
    this.clientServicesService
      .getClientDepartments(this.patient.client_responsible_id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((departments) => {
        this.clientDepartments = departments;
        if (this.clientDepartments.length === 0) {
          this.basicinfoForm.get('client_department').disable();
        }
      });
  }

  setBasicFormlisteners(): void {
    this.basicinfoForm
      .get('email')
      .valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe((email) => {
        this.showChangeEmailButton = email !== this.patient.email;
      });
    this.basicinfoForm
      .get('status')
      .get('label')
      .valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe((status) => {
        this.basicinfoForm.get('programInput').enable();
        this.basicinfoForm
          .get('status')
          .get('deceased')
          .setValue(status === 'Inactive - Deceased' || status === 'Deceased');
        this.handleInactiveOrDeceasedStatus(status);
        this.handlePendingDeviceReturnStatus(status);
      });
  }

  handleInactiveOrDeceasedStatus(status: string): void {
    // Null-guard: the forced-choice flow clears the label (setValue(null)), and this can be
    // invoked defensively with an empty value.
    if (!status) {
      return;
    }
    // Pending Activation is exempt, for the same reason it is exempt from the initial
    // programInput.disable() above: the patient is inactive precisely BECAUSE their programs are what
    // gets assigned next, and the enrollment flow now writes those programs onto the chart. Without
    // this exemption the label's own name defeats it -- 'Inactive - Pending Activation' contains
    // 'Inactive', so selecting it from the dropdown would wipe the very programs it exists to hold.
    if (status === PENDING_ACTIVATION_LABEL) {
      return;
    }
    if (status.includes('Inactive') || status === 'Deceased' || status.includes('Closed - Never Engaged')) {
      const formValue = this.basicinfoForm.getRawValue();
      Object.entries(formValue.status).forEach(([key, value]) => {
        if (value === true && key !== 'deceased') {
          this.basicinfoForm.get('status').get(key).setValue(false);
        }
      });
      this.basicinfoForm.get('programInput').reset();
      this.basicinfoForm.get('programInput').disable();
    }
  }

  handlePendingDeviceReturnStatus(status: string): void {
    if (status === 'Inactive - Pending Device Return') {
      const formValue = this.basicinfoForm.getRawValue();
      const statusPrograms = Object.entries(formValue.status)
        .filter(([key, value]) => key !== 'deceased' && typeof value === 'boolean')
        .reduce((obj, [key, value]) => {
          obj[key] = value;
          return obj;
        }, {});

      const isOnlyRPM =
        statusPrograms['rpm'] === true &&
        Object.entries(statusPrograms)
          .filter(([key]) => key !== 'rpm')
          .every(([_, value]) => value === false);

      if (isOnlyRPM) {
        this.basicinfoForm.get('rpm').setValue(false);
        this.basicinfoForm.get('programInput').disable();
        this.basicinfoForm.get('programInput').reset();
      }
    }
  }

  async saveBasicInfo(): Promise<void> {
    let ccmToApcmConsentData;
    const priorPatient = { ...this.patient };
    const priorCcm      = priorPatient.status?.ccm       ?? false;
    const priorRpm      = priorPatient.status?.rpm       ?? false;
    const priorPcm      = priorPatient.status?.pcm       ?? false;
    const priorApcm     = priorPatient.status?.apcm      ?? false;
    const priorTierApcm = priorPatient.status?.tier_apcm || null;
    const formValue = this.basicinfoForm.getRawValue() as any;
    const { ccm_to_apcm_consent, direct_referral, ...restOfForm } = formValue;
    const filteredStatus: Record<string, any> = {};
    Object.entries(formValue.status).forEach(([key, value]) => {
      if (value !== '') {
        filteredStatus[key] = value;
      }
    });
    const { dateOnHold, ...rest } = filteredStatus;
    const departmentData = this.clientDepartments.find((department) => department.name === formValue.client_department);

    if (this.patient?.status?.ccm) {
      const alreadyHasConsent = this.patient?.ccm_to_apcm_consent?.consent === true;

      if (alreadyHasConsent) {
        ccmToApcmConsentData = this.patient.ccm_to_apcm_consent;
      } else if (ccm_to_apcm_consent === true) {
        ccmToApcmConsentData = {
          consent: true,
          consent_by: `${this.authService.user?.firstName} ${this.authService.user?.lastName}`,
          consent_date: firebase.firestore.Timestamp.now(),
        };
      } else {
        ccmToApcmConsentData = { consent: false };
      }
    }

    const directReferralData = this.buildDirectReferralData(priorPatient, direct_referral === true);

    this.patient = {
      ...restOfForm,
      ...(departmentData && {
        department: {
          id: departmentData?.id,
          name: departmentData?.name,
          athena_department_id: departmentData?.athena_department_id || '',
          athena_department_name: departmentData?.athena_department_name || '',
        },
      }),
      ...(this.patient?.status?.ccm && ccmToApcmConsentData && { ccm_to_apcm_consent: ccmToApcmConsentData }),
      ...directReferralData,
      status: {
        ...rest,
        ...(formValue.status.dateOnHold &&
          formValue.status.label === 'Active - On Hold' && {
            on_hold: { start_date: new Date(), end_date: dateOnHold },
          }),
      },
      dates: this.patient.dates ?? {},
      user_id: this.patient.user_id,
      provider_id: this.patient.provider_id ?? '',
      providers: this.patient.providers ?? [],
      start_last_pregnancy: this.determinePregnancyStartDate(priorPatient),
    };

    const statusEntries = Object.entries(this.patient.status).filter(
      ([key, value]) => typeof value === 'boolean' && key !== 'deceased' && key !== 'active' && key !== 'label'
    );

    const compliance = await this.getPatientCompliance(this.patient.user_id);
    for (const [key, value] of statusEntries) {
      if (priorPatient.status[key] !== this.patient.status[key]) {
        this.patient.status[`${key}_last_update`] = new Date();
      }
      if (priorPatient.status[key] && !this.patient.status[key]) {
        //check clinical time
        const { hasGap, data } = this.patientHasGap(key, compliance);

        if (hasGap) {
          await this.openClinicalTimeAlertDialog(data, key, priorPatient, this.patient);
        } else {
          await this.openDisenrollmentDialog(priorPatient, this.patient, key);
        }
      }
    }

    const programValues = this.availablePrograms.map((program) => this.patient.status[program.value]);
    // The label has to be checked too: a patient on an inactive status can still hold programs
    // (created that way by the enrollment flow), and status.active drives the backend jobs.
    if (this.isStoredLabelActive() && (programValues.some((value) => value) || this.patient.status?.rpm)) {
      this.patient.status.active = true;
    } else {
      this.patient.status.active = false;
      // patient-status-v2: never auto-stamp the deprecated 'Inactive - Other'. Forcing an inactive
      // reason on full program removal is always mandatory — Save is blocked (required, red) until
      // the clinician picks a valid inactive label — so a program-less patient can't be saved with a
      // stale active label; we persist whatever inactive reason they chose.
    }
    this.patient.dob =
      typeof this.patient.dob === 'string'
        ? this.patient.dob
        : `${this.patient.dob.getMonth() + 1}/${this.patient.dob.getDate()}/${this.patient.dob.getFullYear()}`;
    if (this.patient.dates) {
      this.patient.dates.dob = this.patient.dob;
    }
    this.toSavePatient.emit(this.patient);
    // Emit journeyUpdateNeeded so the parent can trigger the mid-month job AFTER
    // the Firestore write completes. Triggering before the write causes a race
    // condition where the backend reads the old program status.
    const programOrTierChanged =
      (this.patient.status?.ccm ?? false) !== priorCcm ||
      (this.patient.status?.rpm ?? false) !== priorRpm ||
      (this.patient.status?.pcm ?? false) !== priorPcm ||
      (this.patient.status?.apcm ?? false) !== priorApcm ||
      (this.patient.status?.tier_apcm || null) !== priorTierApcm;
    if (programOrTierChanged && this.patient.user_id) {
      this.journeyUpdateNeeded.emit();
    }
    if (this.basicinfoForm.get('status').get('label').value !== 'Active - On Hold') {
      this.basicinfoForm.get('status').get('dateOnHold').setValue('');
    }
    if (this.patient?.ccm_to_apcm_consent?.consent === true) {
      this.basicinfoForm.get('ccm_to_apcm_consent')?.disable();
    }
    if (this.patient?.direct_referral === true) {
      this.basicinfoForm.get('direct_referral')?.disable();
    }
  }

  async getPatientCompliance(patientId: string): Promise<PatientGap> {
    try {
      const compliance = await this.patientService.getPatientCompliance(patientId).toPromise();
      return compliance;
    } catch (error) {
      console.error('Error fetching patient compliance:', error);
      return null;
    }
  }

  patientHasGap(
    program: string,
    compliance: PatientGap
  ): { hasGap: boolean; data: { program: string; uniqueMeasures: any; timeReport: any } } {
    if (!compliance) {
      return { hasGap: false, data: { program: '', uniqueMeasures: 0, timeReport: 0 } };
    }
    if (program === 'rpm') {
      return {
        hasGap: (compliance.uniqueMeasures > 13 && compliance.uniqueMeasures < 16) || compliance.time.rpm.hasGap,
        data: {
          program: 'rpm',
          uniqueMeasures: compliance.uniqueMeasures,
          timeReport: Number(compliance.time.rpm.total),
        },
      };
    }
    if (program === 'ccm') {
      return {
        hasGap: compliance.time.ccm.hasGap,
        data: {
          program: 'ccm',
          uniqueMeasures: compliance.uniqueMeasures,
          timeReport: Number(compliance.time.ccm.total),
        },
      };
    }
    return { hasGap: false, data: { program: '', uniqueMeasures: 0, timeReport: 0 } };
  }

  openChangeEmailDialog(): void {
    // open dialog to confirm deactivation
    const modal = this.dialog.open(AreYouSureModalComponent, {
      data: {
        title: `Do you want to change the patient's login email?`,
        body: `The user will need to log in again with the new email.`,
        hideCancelBtn: false,
        cancelButton: 'Cancel',
        confirmButton: 'Yes, change the login email',
      },
    });
    // refresh the page after closed
    modal.afterClosed().subscribe(async (result) => {
      if (result) {
        this.changeEmail.emit({ email: this.basicinfoForm.get('email').value, patientId: this.patient.user_id });
        this.showChangeEmailButton = false;
      }
    });
  }

  openProviderPanel(): void {
    const dialogRef = this.dialog.open(AddProvidersComponent, {
      width: '700px',
      data: { providers: this.patient.providers },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.patientService
          .updatePatientProviders(result, this.patient.user_id, this.authService.user?.user_id)
          .subscribe({
            next: () => {
              this.patient.providers = result;
              this.patient.providers_ids = result.map((provider) => provider.uid ?? '') ?? [];
              this.patient.provider_id = result.find((provider) => provider.rank === 1)?.uid ?? '';
              this.snackService.genericSnackBar('Providers updated successfully', ['success-snackbar'], 3000);
            },
            error: (error) => {
              console.error('Error updating patient providers:', error);
              this.snackService.genericSnackBar('Failed to update providers', ['error-snackbar'], 3000);
            },
          });
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  patientChurn(priorPatient: Patient, patient: Patient, reason: string, program: string): void {
    const lastUpdateProgram = `${program}_last_update`;
    const patientChurn = {
      patient_id: this.patient.user_id,
      client_id: priorPatient.client_responsible_id,
      care_manager_uid: patient.providers[0].uid,
      pes_rep: priorPatient.pes ? priorPatient.pes : { firstName: '', email: '', lastName: '' },
      program_start_date: priorPatient.status[lastUpdateProgram].toDate(),
      program_end_date: this.patient.status[lastUpdateProgram],
      term_reason: reason ?? '',
      program,
    };
    this.patientService
      .patientChurn(patientChurn)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {},
        error: (error) => {
          console.error('Error updating patient churn:', error);
        },
      });
  }

  /** Unknown labels default to active, so an orphan or an unloaded catalog cannot deactivate a patient. */
  private isStoredLabelActive(): boolean {
    const label = this.patient?.status?.label;
    return this.statusCatalog.find((item) => item.label === label)?.active ?? true;
  }

  private buildStatusGroups(options: string[]): { label: string; options: string[] }[] {
    const active: string[] = [];
    const inactive: string[] = [];
    (options ?? []).forEach((status) => {
      if (status?.toLowerCase().startsWith('active')) {
        active.push(status);
      } else {
        inactive.push(status);
      }
    });
    const groups: { label: string; options: string[] }[] = [];
    // Sorted here because getPatientStatusOptions(), which used to sort the whole list, is no longer
    // the source.
    if (active.length) {
      groups.push({ label: 'Active', options: active.sort((a, b) => a.localeCompare(b)) });
    }
    if (inactive.length) {
      groups.push({ label: 'Inactive', options: inactive.sort((a, b) => a.localeCompare(b)) });
    }
    return groups;
  }

  private initStatusGroupExpansion(groups: { label: string; options: string[] }[]): void {
    const currentLabel = this.basicinfoForm?.get('status')?.get('label')?.value;
    // Expand the group that holds the current value so it is visible on open. If the
    // stored value is a legacy status no longer present in any group (or there is no
    // value), fall back to expanding Active so the panel never opens fully collapsed.
    const currentGroup = groups.find((group) => group.options.includes(currentLabel));
    groups.forEach((group) => {
      if (this.groupExpanded[group.label] === undefined) {
        this.groupExpanded[group.label] = currentGroup ? group.label === currentGroup.label : group.label === 'Active';
      }
    });
  }

  /**
   * Forced inactive-reason choice: when every program is removed from an active patient, clear the
   * status label and require a valid inactive reason so Save stays blocked until one is chosen.
   * Adding a program back releases the requirement. Always mandatory; the catalog is the only guard.
   */
  private evaluateForcedInactiveChoice(): void {
    if (this.statusCatalog.length === 0) {
      return;
    }
    const statusGroup = this.basicinfoForm.get('status') as UntypedFormGroup;
    const labelControl = statusGroup.get('label');
    if (!this.hasAnyActiveProgram() && !this.acceptableInactiveLabels().includes(labelControl.value)) {
      // No programs left and the current label isn't a valid inactive reason (it's active,
      // deprecated, orphan or empty): clear it and show the forced inactive-reason selector. Covers
      // e.g. a patient stuck on a deprecated label who still had programs. Validity/red is driven by
      // forcedInactiveChoiceValidator.
      this.forcedInactiveEngaged = true;
      labelControl.setValue(null, { emitEvent: false });
    } else if (this.hasAnyActiveProgram() && this.forcedInactiveEngaged) {
      this.forcedInactiveEngaged = false;
    }
    // Re-run the group validator so the error (and the field's red state) update immediately.
    statusGroup.updateValueAndValidity();
  }

  private hasAnyActiveProgram(): boolean {
    // The status group's validator runs while the form is still being constructed (inside
    // fb.group()), before `basicinfoForm` is assigned — so guard against an undefined form.
    const statusGroup = this.basicinfoForm?.get('status') as UntypedFormGroup;
    if (!statusGroup) {
      return false;
    }
    const status = statusGroup.getRawValue();
    return this.availablePrograms.some((program) => status[program.value] === true) || status.rpm === true;
  }

  /**
   * Labels that do NOT require forcing an inactive-reason choice: the selectable inactive reasons
   * plus deceased (a valid terminal state). Anything else — an active, deprecated, orphan or empty
   * label — must be resolved to a valid inactive reason once the patient has no programs. Kept in
   * sync with the forcedInactiveChoiceValidator's allowedLabels so the red UI and the Save block
   * engage on exactly the same set.
   */
  private acceptableInactiveLabels(): string[] {
    return [
      ...this.forcedInactiveOptions.map((option) => option.label),
      ...this.statusCatalog.filter((item) => item.deceased).map((item) => item.label),
    ];
  }

  /**
   * If the patient's stored status label is a legacy alias (matches a catalog entry via
   * legacyLabels but is not its canonical label), switch the control to the canonical label so it
   * displays and is selectable instead of rendering blank. Silent (emitEvent:false) — it only
   * persists if the clinician saves. Labels not in the catalog at all are left untouched.
   */
  private normalizeStoredStatusLabel(): void {
    const labelControl = this.basicinfoForm?.get('status')?.get('label');
    const current = labelControl?.value;
    if (!labelControl || !current || this.statusCatalog.length === 0) {
      return;
    }
    const entry = this.statusCatalog.find(
      (item) => item.label === current || (item.legacyLabels ?? []).includes(current)
    );
    if (entry && entry.label !== current) {
      labelControl.setValue(entry.label, { emitEvent: false });
    }
  }

  /**
   * Builds the Direct Referral data to persist, enforcing the one-way lock:
   * once enabled, the original value and audit metadata (who/when) are preserved
   * and can never be changed; on first activation the current user and timestamp
   * are captured; otherwise it stays disabled.
   */
  private buildDirectReferralData(
    priorPatient: Patient,
    selectedReferral: boolean
  ): Pick<Patient, 'direct_referral' | 'direct_referral_date' | 'direct_referral_by'> {
    const alreadyReferred = priorPatient?.direct_referral === true;
    if (alreadyReferred) {
      return {
        direct_referral: true,
        direct_referral_date: priorPatient.direct_referral_date ?? null,
        direct_referral_by: priorPatient.direct_referral_by ?? '',
      };
    }
    if (selectedReferral) {
      return {
        direct_referral: true,
        direct_referral_by: `${this.authService.user?.firstName ?? ''} ${this.authService.user?.lastName ?? ''}`.trim(),
        direct_referral_date: firebase.firestore.Timestamp.now(),
      };
    }
    return { direct_referral: false };
  }

  private async openDisenrollmentDialog(priorPatient: Patient, patient: Patient, key: string) {
    return new Promise((resolve, reject) => {
      // open dialog to confirm deactivation
      const dialog = this.dialog.open(PatientDisenrollmentComponent, {
        width: '600px',
        disableClose: true,
        data: {
          program: key,
        },
      });
      dialog.afterClosed().subscribe((result) => {
        const disenrollmentCommentKey = `disenrollment_${key}_comments`;
        this.patient.status[disenrollmentCommentKey] = result?.reason ?? '';
        this.patientChurn(priorPatient, patient, result?.reason, key);
        resolve(true);
      });
    });
  }

  private async openClinicalTimeAlertDialog(
    data: { program: string; uniqueMeasures: number; timeReport: number },
    program: string,
    priorPatient: Patient,
    patient: Patient
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const dialog = this.dialog.open(ClinicalTimeAlertComponent, {
        width: '600px',
        disableClose: true,
        data,
      });
      dialog.afterClosed().subscribe(async (result) => {
        if (result?.disenroll) {
          await this.openDisenrollmentDialog(priorPatient, patient, program);
          resolve(true);
        } else if (result && !result.disenroll) {
          const isLead = this.authService.user?.roles.isManagerNurse;
          const leadId = isLead ? this.authService.user?.user_id : this.authService.user?.lead?.id;
          this.patientService
            .sendClinicalTimeAlert({
              assignee_id: leadId,
              creator_id: this.authService.user?.user_id,
              client_id: this.patient.client_responsible_id,
              client_name: (this.patient.client_responsible ?? '').trim(),
              client_name_lower: (this.patient.client_responsible ?? '').trim().toLowerCase(),
              patient_id: this.patient.user_id,
              patient_name: this.patient.firstName + ' ' + this.patient.lastName,
              patient_name_lower: (this.patient.firstName + ' ' + this.patient.lastName).toLowerCase(),
              task_name: `Patient Disenrollment Review - ${data.program.toUpperCase()}`,
              task_name_lower: `patient disenrollment review - ${data.program.toLowerCase()}`,
              description:
                'The patient is nearing the monthly closure threshold in the ' +
                data.program.toUpperCase() +
                '. Review the case and confirm whether disenrollment should proceed.',
              priority: 'high',
              completed: false,
              expired: false,
              due_date: new Date(),
              create_date: new Date(),
              last_update: new Date(),
            } as ClinicalTask)
            .subscribe({
              next: (res) => {
                resolve(true);
                this.revertProgram(program, priorPatient);
              },
              error: (error) => {
                console.error('Failed to send clinical time alert:', error);
                reject(error);
              },
            });
        } else {
          resolve(true);
          this.revertProgram(program, priorPatient);
        }
      });
    });
  }

  private fillEMRdataFromPatient(): string {
    return this.patient?.emr_data ? this.patient?.emr_data.mrn : this.patient?.emr_id;
  }

  private fillEMRdataGUIDFromPatient(): string {
    return this.patient?.emr_data ? this.patient?.emr_data?.emr_guid : this.patient?.emr_id;
  }

  /**
   *
   * Determines the pregnancy start date based on previous and current pregnancy status
   *
   * @param priorPatient The patient object before form changes
   * @returns Date object if newly pregnant, existing date if already pregnant, or null if not pregnant
   */
  private determinePregnancyStartDate(priorPatient: Patient): Date | null {
    const wasPregnant = !!priorPatient.isPregnant;
    const isNowPregnant = this.basicinfoForm.get('isPregnant').value;

    if (!wasPregnant && isNowPregnant) {
      return new Date();
    }
    if (wasPregnant && isNowPregnant) {
      return priorPatient.start_last_pregnancy || null;
    }
    return null;
  }
}
